import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, fmtNum } from "@/lib/api";
import { PageHeader, Card, StatusBadge } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Layers, Lock, LockOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STACK_MGR = ["Super Admin", "Management / Owner", "Warehouse Manager", "Stack Supervisor"];

function fillColor(u, allowed = 100) {
  if (u <= 0) return "bg-stone-100";
  if (u >= allowed) return "bg-red-200";
  if (u >= allowed * 0.85) return "bg-amber-200";
  if (u >= 50) return "bg-emerald-300";
  return "bg-emerald-200";
}
function fillBar(u, allowed = 100) {
  if (u >= allowed) return "bg-red-500";
  if (u >= allowed * 0.85) return "bg-amber-500";
  return "bg-emerald-600";
}

export default function Stacks() {
  const [sel, setSel] = useState(null);
  const qc = useQueryClient();
  const { user } = useAuth();
  const canMarkComplete = STACK_MGR.includes(user?.role);

  const { data: stacks, isLoading } = useQuery({
    queryKey: ["stacks-view"],
    queryFn: async () => (await api.get("/reports/stacks")).data,
  });

  const toggleComplete = async (stack, complete) => {
    try {
      await api.post(`/masters/stacks/${stack.id}/stack-complete`, { stack_complete: complete });
      toast.success(complete ? "Stack marked complete" : "Stack reopened for inward");
      qc.invalidateQueries({ queryKey: ["stacks-view"] });
      if (sel?.id === stack.id) {
        const { data } = await api.get("/reports/stacks");
        const updated = data.find((s) => s.id === stack.id);
        if (updated) setSel(updated);
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const grouped = {};
  (stacks || []).forEach((s) => {
    (grouped[s.warehouse_name] = grouped[s.warehouse_name] || []).push(s);
  });

  return (
    <div data-testid="stacks-page">
      <PageHeader title="Stack Layout" subtitle="Space-based occupancy — mixed bag types share one stack footprint (±10% tolerance). Click a stack for details." />

      {isLoading ? (
        <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-emerald-700" size={32} /></div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([wh, list]) => (
            <div key={wh}>
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-stone-700">
                <Layers size={16} className="text-emerald-700" /> {wh}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {list.map((s) => {
                  const occ = s.occupancy || {};
                  const space = occ.space_used_pct ?? occ.utilisation ?? 0;
                  const allowed = occ.space_allowed_pct ?? 110;
                  const complete = s.stack_complete || occ.stack_complete;
                  const fillPct = Math.min((space / allowed) * 100, 100);
                  return (
                    <button
                      key={s.id}
                      data-testid={`stack-${s.code}`}
                      onClick={() => setSel(s)}
                      className={`relative h-32 overflow-hidden rounded-xl border text-left shadow-sm transition-transform hover:scale-[1.02] hover:shadow-md ${complete ? "border-amber-400 bg-amber-50/30" : "border-stone-200 bg-white"}`}
                    >
                      <div className={`absolute bottom-0 left-0 right-0 ${fillColor(space, allowed)}`} style={{ height: `${fillPct}%` }} />
                      <div className="relative z-10 flex h-full flex-col justify-between p-3">
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-display text-xl font-extrabold text-stone-900">{s.code}</span>
                          <div className="flex flex-col items-end">
                            {complete && <Lock size={12} className="text-amber-700 mb-0.5" />}
                            <span className="text-xs font-bold text-stone-700">{space}%</span>
                          </div>
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-stone-800">{fmtNum(occ.current_bags)} bags</div>
                          <div className="text-[10px] uppercase tracking-wide text-stone-500">
                            {complete ? "Complete" : space <= 0 ? "Empty" : `${s.lots.length} lot(s)`}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg" data-testid="stack-detail-dialog">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2 flex-wrap">
                  Stack {sel.code}
                  {(sel.stack_complete || sel.occupancy?.stack_complete) && (
                    <StatusBadge tone="warning">Complete</StatusBadge>
                  )}
                  <StatusBadge tone={sel.occupancy.utilisation >= 100 ? "danger" : sel.occupancy.utilisation >= 85 ? "warning" : "emerald"}>
                    {sel.occupancy.space_used_pct ?? sel.occupancy.utilisation}% space
                  </StatusBadge>
                </DialogTitle>
              </DialogHeader>
              <div className="text-sm text-stone-500 -mt-2">
                {sel.warehouse_name} · {sel.capacity_config}
                {sel.occupancy.area_sqft ? ` · ${fmtNum(sel.occupancy.area_sqft)} sqft` : ""}
                {sel.occupancy.tolerance_pct != null ? ` · ±${sel.occupancy.tolerance_pct}% tolerance` : ""}
              </div>
              <div className="grid grid-cols-2 gap-3 my-2">
                <Stat label="Total bags" value={fmtNum(sel.occupancy.current_bags)} />
                <Stat label="Weight" value={`${fmtNum(sel.occupancy.current_weight)} Qtl`} />
                <Stat label="Space used" value={`${sel.occupancy.space_used_pct ?? sel.occupancy.utilisation}%`} />
                <Stat label="Max allowed" value={`${sel.occupancy.space_allowed_pct ?? 110}%`} />
              </div>
              <div className="h-2 w-full rounded-full bg-stone-100 overflow-hidden">
                <div
                  className={`h-full ${fillBar(sel.occupancy.space_used_pct ?? sel.occupancy.utilisation, sel.occupancy.space_allowed_pct ?? 110)}`}
                  style={{ width: `${Math.min(((sel.occupancy.space_used_pct ?? 0) / (sel.occupancy.space_allowed_pct ?? 110)) * 100, 100)}%` }}
                />
              </div>
              {(sel.occupancy.by_bag_type || []).length > 0 && (
                <div className="mt-3">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-stone-400 mb-2">By bag type</div>
                  <div className="space-y-1.5">
                    {sel.occupancy.by_bag_type.map((b, i) => (
                      <div key={i} className="flex justify-between text-sm rounded-lg bg-stone-50 px-3 py-2">
                        <span className="text-stone-600">{b.bag_type_name}</span>
                        <span className="font-semibold">{fmtNum(b.current_bags)} / {fmtNum(b.max_bags)} ({b.space_share_pct}% space)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {canMarkComplete && (
                <div className="mt-3 flex gap-2">
                  {(sel.stack_complete || sel.occupancy?.stack_complete) ? (
                    <button
                      type="button"
                      data-testid="stack-reopen-btn"
                      onClick={() => toggleComplete(sel, false)}
                      className="flex flex-1 h-10 items-center justify-center gap-2 rounded-lg border border-stone-300 text-sm font-semibold text-stone-700 hover:bg-stone-50"
                    >
                      <LockOpen size={16} /> Reopen stack
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="stack-complete-btn"
                      onClick={() => {
                        if (!window.confirm(`Mark stack ${sel.code} as complete? No further inward or transfer will be allowed.`)) return;
                        toggleComplete(sel, true);
                      }}
                      className="flex flex-1 h-10 items-center justify-center gap-2 rounded-lg bg-amber-600 text-sm font-semibold text-white hover:bg-amber-700"
                    >
                      <Lock size={16} /> Mark stack complete
                    </button>
                  )}
                </div>
              )}
              <div className="mt-4">
                <div className="text-[11px] font-bold uppercase tracking-widest text-stone-400 mb-2">Lots in this stack</div>
                {sel.lots.length === 0 ? (
                  <div className="text-sm text-stone-400">This stack is empty.</div>
                ) : (
                  <div className="space-y-2">
                    {sel.lots.map((l, i) => (
                      <div key={i} className="rounded-lg border border-stone-200 p-3">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs font-semibold text-emerald-700">{l.lot_number}</span>
                          <span className="text-sm font-semibold text-stone-900">{fmtNum(l.bags)} bags</span>
                        </div>
                        <div className="mt-1 text-xs text-stone-500">{l.party} · {l.item} · {fmtNum(l.weight)} Qtl</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-lg bg-stone-50 p-2.5">
    <div className="text-[11px] text-stone-500">{label}</div>
    <div className="font-semibold text-stone-900">{value}</div>
  </div>
);
